import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Menu, 
  X, 
  User, 
  BookOpen, 
  LogOut, 
  Settings,
  Home,
  GraduationCap,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setShowUserMenu(false);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
  };

  const languages = {
    fr: { name: 'Français', flag: '🇫🇷' },
    zh: { name: '中文', flag: '🇨🇳' }
  };

  const currentLanguage = languages[i18n.language] || languages.fr;

  const navLinks = [
    // TODO: Réactiver quand il y aura plusieurs pages
    // { name: t('nav.home'), href: '/', icon: Home },
    // { name: t('nav.courses'), href: '/courses', icon: BookOpen },
    // ...(isAuthenticated ? [
    //   { name: t('nav.myCourses'), href: '/my-courses', icon: GraduationCap },
    //   { name: t('nav.dashboard'), href: '/dashboard', icon: User }
    // ] : [])
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-max">
        <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/logo.png" 
              alt="La Source et La Voie" 
              className="h-12 w-auto transition-transform group-hover:scale-105"
            />
            <span className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors hidden sm:block">
              {t('hero.title')}
            </span>
          </Link>

          {/* Navigation desktop */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    isActivePath(link.href)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{link.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Language Selector & Actions utilisateur */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">{currentLanguage.flag}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {languageMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                  {Object.entries(languages).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => changeLanguage(code)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2 transition-colors ${
                        i18n.language === code ? 'bg-primary-50 text-primary-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.firstName?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {user?.firstName}
                  </span>
                </button>

                {showUserMenu && (
                  <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50"
                    style={{ zIndex: 9999 }}
                  >
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Profil
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-none bg-transparent text-left"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // TODO: Réactiver quand le contenu sera prêt
              <div className="flex items-center space-x-3">
                {/* Boutons masqués temporairement */}
              </div>
              // <div className="flex items-center space-x-3">
              //   <Link
              //     to="/login"
              //     className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
              //   >
              //     Connexion
              //   </Link>
              //   <Link
              //     to="/register"
              //     className="btn btn-primary"
              //   >
              //     S'inscrire
              //   </Link>
              // </div>
            )}
          </div>

          {/* Bouton menu mobile */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Menu mobile */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-4 space-y-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                        isActivePath(link.href)
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{link.name}</span>
                    </Link>
                  );
                })}

                {/* Language Selector Mobile */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 px-3 mb-2">Langue / Language / 语言</p>
                  {Object.entries(languages).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => {
                        changeLanguage(code);
                        setIsOpen(false);
                      }}
                      className={`flex items-center space-x-2 w-full px-3 py-2 rounded-md transition-colors ${
                        i18n.language === code ? 'text-primary-600 bg-primary-50 font-bold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>

                {/* Actions utilisateur mobile */}
                <div className="pt-4 border-t border-gray-200">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 px-3 py-2">
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {user?.firstName?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {user?.firstName} {user?.lastName}
                        </span>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md"
                      >
                        <Settings className="w-5 h-5" />
                        <span>Profil</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md cursor-pointer"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Déconnexion</span>
                      </button>
                    </div>
                  ) : null
                  // Masqué pour site statique - pas d'authentification
                  // (
                  //   <div className="space-y-2">
                  //     <Link
                  //       to="/login"
                  //       onClick={() => setIsOpen(false)}
                  //       className="block px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md font-medium"
                  //     >
                  //       Connexion
                  //     </Link>
                  //     <Link
                  //       to="/register"
                  //       onClick={() => setIsOpen(false)}
                  //       className="block px-3 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-md font-medium text-center"
                  //     >
                  //       S'inscrire
                  //     </Link>
                  //   </div>
                  // )
                  }
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Overlay pour fermer les menus */}
      {(showUserMenu || languageMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setLanguageMenuOpen(false);
          }}
        />
      )}
    </nav>
  );
}

export default Navbar;
